// Feedback reports on Changeling's server: filing one (any signed-in user),
// and reading and forwarding them (the deployment's operators).
import { api, transport } from './http';
import type { LogLine } from '../feedback/logs';
import type { PickedElement, ReportBody } from '../feedback/report';

export interface Reporter {
  id: number;
  /** As last recorded; null when the reporter is no longer known. */
  login: string | null;
}

export interface ReportSummary {
  id: number;
  created_at: string;
  reporter: Reporter;
  /** The note's opening. */
  excerpt: string;
  route: string;
  has_screenshot: boolean;
  has_logs: boolean;
  has_element: boolean;
  /** The issue it was forwarded to. */
  forwarded: string | null;
}

export interface Report {
  id: number;
  created_at: string;
  reporter: Reporter;
  note: string;
  route: string;
  client_version: string;
  server_version: string;
  browser: string;
  logs: LogLine[] | null;
  element: PickedElement | null;
  screenshot: { type: string; bytes: number } | null;
  forwarded: { url: string; at: string } | null;
}

export function fileFeedback(body: ReportBody): Promise<{ report: { id: number; created_at: string } }> {
  return api('/api/feedback', { method: 'POST', body });
}

export function listFeedback(before?: number): Promise<{ reports: ReportSummary[]; more: boolean }> {
  return api(`/api/feedback${before ? `?before=${before}` : ''}`);
}

export async function getFeedback(id: number): Promise<Report> {
  return (await api<{ report: Report }>(`/api/feedback/${id}`)).report;
}

/** Where the report's screenshot is served (operators only). */
export function screenshotUrl(id: number): string {
  return `/api/feedback/${id}/screenshot`;
}

/** An issue as a forward would create it. */
export interface IssueDraft {
  title: string;
  body: string;
}

/** The issue a report would become, for the operator to edit: nothing private, nothing GitHub would act on; with `screenshot`, a link to the report here. */
export async function issueDraft(id: number, screenshot: boolean): Promise<IssueDraft> {
  return (await api<{ issue: IssueDraft }>(`/api/feedback/${id}/issue?screenshot=${screenshot}`)).issue;
}

/** Creates the issue on `repo` as the operator edited it. */
export function forwardFeedback(id: number, repo: string, issue: IssueDraft): Promise<{ issue: { number: number; url: string } }> {
  return api(`/api/feedback/${id}/forward`, { method: 'POST', body: { repo, title: issue.title, body: issue.body } });
}

/** The report's screenshot and its type, fetched with the session (an `<img>` would bypass mock mode). */
export async function fetchScreenshot(id: number): Promise<{ bytes: Uint8Array; type: string }> {
  const res = await transport(screenshotUrl(id), { method: 'GET', headers: { Accept: 'image/png, image/jpeg' } });
  if (!res.ok) throw new Error(`The screenshot could not be loaded (HTTP ${res.status}).`);
  const type = res.headers.get('content-type') ?? '';
  if (type !== 'image/png' && type !== 'image/jpeg') throw new Error('The screenshot is not a PNG or JPEG image.');
  return { bytes: new Uint8Array(await res.arrayBuffer()), type };
}
